import { createAction } from '@reduxjs/toolkit'
import { all, call, ForkEffect, put, select, takeEvery } from 'redux-saga/effects'

import { getFlagTypeById } from '../utils/getFlagTypeById'
import { setActiveFlagAction } from './setActiveFlag'
import { createFlag } from '../../../api/projects-api'
import {
    FlagAPIPayload,
    FlagAPIResponse,
    FlagFilters,
    NewOpeningLink,
    NormalizedFlag,
    OpeningLink,
} from '../../../models/flags'
import { isFlagVisibleBasedOnFilters } from '../../../shared/services/flags/flags-service'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Flag } from '../../lib/toolBoxes/2D'
import { selectActiveDocumentChunkIds } from '../../slices/documents'
import { addNewFlag, selectFlagFilters, selectMaterialFlags } from '../../slices/materialFlags'
import { CFB_LABEL } from '../../utils/constants'

const BASE_SHIFT = 10
const SHIFT_MULTIPLIER = 1.5

export const copyFlagAction = createAction<{
    flagId: number
    newUserEmail: string
    newUserName: string
    label: string
    copyCount: number
}>('copyFlag')

export function* handleCopyFlag({ payload }: ReturnType<typeof copyFlagAction>) {
    try {
        const manager: PaperManager | null = yield call(managers.get2DManager)

        if (!manager) return
        const flagTool: Flag = yield call(manager.getTool, Flag.NAME)

        const activeFloorIds: number[] = yield select(selectActiveDocumentChunkIds)
        const flags: NormalizedFlag[] = yield select(selectMaterialFlags)

        const foundFlag = flags.find((f) => f.id === payload.flagId)

        if (!foundFlag) return

        const firstOpenLink: NewOpeningLink = foundFlag.opening_links[0]

        // create a flag open link with new coordinates + small shift and with unlinked opening_id
        const openLinkToCreate: NewOpeningLink = {
            opening_id: null,

            coordinates: [
                firstOpenLink.coordinates[0] + BASE_SHIFT * payload.copyCount * SHIFT_MULTIPLIER,
                firstOpenLink.coordinates[1] - BASE_SHIFT * payload.copyCount * SHIFT_MULTIPLIER,
            ],
            document_chunk_id: firstOpenLink.document_chunk_id,
        }

        const flagToCreateData: FlagAPIPayload = {
            created_by_username: payload.newUserName,
            created_by_user_email: payload.newUserEmail,
            category_id: foundFlag.category?.id ? Number(foundFlag.category.id) : undefined,
            error_id: foundFlag.error?.id ? Number(foundFlag.error.id) : undefined,
            sub_category_id:
                foundFlag.status && foundFlag.sub_category?.id ? Number(foundFlag.sub_category.id) : undefined,
            severity_id: foundFlag.severity?.id ? Number(foundFlag.severity.id) : undefined,
            status_id: foundFlag.status?.id ? Number(foundFlag.status.id) : undefined,
            type_id: Number(foundFlag.type?.id),
            description: foundFlag.description,
            opening_links: [openLinkToCreate],
            ...(payload.label.toLowerCase() === CFB_LABEL && { submitter_name: foundFlag.submitter_name }),
        }

        const response: FlagAPIResponse = yield call(
            createFlag,
            foundFlag.project_id,
            flagToCreateData as FlagAPIPayload
        )

        const updatedOpeningLinks: OpeningLink[] = response?.opening_links.filter((link: OpeningLink) =>
            activeFloorIds.includes(link.document_chunk_id)
        )

        if (!!updatedOpeningLinks?.length) {
            const type = getFlagTypeById(response.type_id ?? 1)

            const flagFilters: FlagFilters = yield select(selectFlagFilters)

            // add saved flags to blueprint
            yield all(
                updatedOpeningLinks.map((link) =>
                    call(
                        flagTool.drawFlagByCoords,
                        link.coordinates,
                        response.id,
                        link.opening_id,
                        response.order,
                        type,
                        isFlagVisibleBasedOnFilters(
                            flagFilters,
                            type,
                            response.status_id,
                            response?.creationJiraStatus?.id
                        ),
                        response.status_id,
                        response?.creationJiraStatus?.id,
                        false,
                        link.id
                    )
                )
            )

            // save flags in store
            yield put(addNewFlag({ flag: response }))

            // save flags in store
            yield put(setActiveFlagAction({ flag_id: response.id }))
        }
    } catch (error) {
        yield call(console.error, error)
    }
}

export function* watchForCopyFlag(): Generator<ForkEffect<never>, void, unknown> {
    yield takeEvery(copyFlagAction.type, handleCopyFlag)
}
